<template>
  <div v-if="value" v-bsl="value"></div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import moment from 'moment';

import TeacherSvg from '@/assets/images/onboarding/classlist.svg';
import AttendancesSymbolsPng from '@/assets/images/onboarding/attendances.png';
import ArrowTopImg from '@/assets/images/onboarding/arrowtop.svg';
import ArrowRightSvg from '@/assets/images/onboarding/arrowright.svg';

export default {
  name: 'AttendancesDesktop',
  props: {
    value: {
      required: true,
    },
    isEmpty: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState('students', ['student']),

    teacherImage() {
      return TeacherSvg;
    },

    attendancesSymbolsImage() {
      return AttendancesSymbolsPng;
    },

    arrowTopImage() {
      return ArrowTopImg;
    },

    arrowRightImage() {
      return ArrowRightSvg;
    },

    onboardingStepOneText() {
      return `
        <div class="flex flex-col justify-center items-center">
          <img style="max-width: 155px" src="${this.teacherImage}"/>
          <h3 class="text-center">${this.$t('onboarding.attendances_step1.title')}</h3>
          <p class="text-center py-4 px-6">${this.$t('onboarding.attendances_step1.content')}</p>
          <p class="text-center pb-4 px-6">
            <span>${this.$t('onboarding.attendances_step1.content2')}</span><span class="text-black">"</span><span class="font-bold text-blue-zoom">${this.$t('onboarding.attendances_step1.content3')}</span><span class="text-black">"</span><span>${this.$t('onboarding.attendances_step1.content4')}</span>
          </p>
        </div>`;
    },

    onboardingStepTwoText() {
      return `
        <div class="flex flex-col justify-center items-center">
          <h3 class="text-center">${this.$t('onboarding.attendances_step2.title')}</h3>
          <p class="text-center py-4 px-8">${this.$t('onboarding.attendances_step2.content')}</p>
          <div class="flex justify-center align-center">
            <img src="${this.attendancesSymbolsImage}"/>
          </div>
        </div>`;
    },
  },
  methods: {
    ...mapMutations('layout', ['showClassList', 'hideClassList']),
    ...mapActions('onboarding', ['setOnboardingProperty']),

    closeOnboardingTag() {
    },

    acceptOnboardingTag() {
    },

    addProgressDots(tour) {
      const currentStepElement = tour.currentStep.el;
      const header = currentStepElement.querySelector('.shepherd-header');
      const progress = document.createElement('span');
      progress.style['margin-right'] = '315px';
      progress.innerText = `${tour.steps.indexOf(tour.currentStep) + 1}/${
        tour.steps.length
      }`;
      header.insertBefore(
        progress,
        currentStepElement.querySelector('.shepherd-cancel-icon'),
      );
    },

    addArrow(tour, direction) {
      const currentStepElement = tour.currentStep.el;
      const header = currentStepElement.querySelector('.shepherd-header');
      const progress = document.createElement('img');
      if (direction === 'top') {
        progress.classList.add('stepherd-custom-arrow--top');
        progress.src = `${this.arrowTopImage}`;
      }
      if (direction === 'right') {
        progress.classList.add('stepherd-custom-arrow--right');
        progress.src = `${this.arrowRightImage}`;
      }
      header.insertBefore(
        progress,
        currentStepElement.querySelector('.shepherd-cancel-icon'),
      );
    },

    getDateWithYearlyOffset(years) {
      return moment().add(years, 'y').toDate();
    },

    setTourCookie() {
      if (this.student?.id) {
        this.setOnboardingProperty({ deviceType: 'desktop', property: 'attendance', value: true });
      }
    },

    startOnboarding() {
      this.$nextTick(() => {
        const vm = this;
        const tour = this.$shepherd({
          useModalOverlay: true,
          defaultStepOptions: {
            cancelIcon: {
              enabled: true,
            },
          },
        });

        if (!vm.student.group.hasHomeworks) {
          tour.addStep({
            text: vm.onboardingStepOneText,
            beforeShowPromise() {
              return new Promise((resolve) => {
                setTimeout(() => {
                  window.scrollTo(0, 0);
                  resolve();
                }, 500);
              });
            },
            attachTo: {
              element: this.isEmpty ? null : '.classList',
              on: this.isEmpty ? null : 'top-end',
            },
            modalOverlayOpeningRadius: 12,
            popperOptions: {
              placement: 'top-end',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 20],
                  },
                },
              ],
            },
            buttons: [
              {
                text: this.$t('onboarding.attendances_step1.btn'),
                action() {
                  vm.acceptOnboardingTag();
                  return this.next();
                },
              },
            ],
            when: {
              show() {
                if (!vm.isEmpty) {
                  vm.addArrow(this.tour, 'top');
                }
                vm.addProgressDots(this.tour);
              },
              cancel() {
                vm.closeOnboardingTag();
                vm.$emit('input', !vm.value);
                vm.setTourCookie();
              },
            },
          });

          tour.addStep({
            text: vm.onboardingStepTwoText,
            beforeShowPromise() {
              return new Promise((resolve) => {
                setTimeout(() => {
                  window.scrollTo(0, 0);
                  resolve();
                }, 500);
              });
            },
            attachTo: {
              element: this.isEmpty ? null : '.attendances__list',
              on: this.isEmpty ? null : 'right-start',
            },
            canClickTarget: false,
            modalOverlayOpeningRadius: 12,
            popperOptions: {
              placement: 'right-start',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 270],
                  },
                },
              ],
            },
            buttons: [
              {
                text: this.$t('onboarding.attendances_step2.btn_rev'),
                secondary: true,
                action() {
                  return new Promise((resolve) => {
                    setTimeout(() => {
                      this.back();
                      resolve();
                    }, 1000);
                  });
                },
              },
              {
                text: this.$t('onboarding.attendances_step2.btn'),
                action() {
                  vm.acceptOnboardingTag();
                  vm.$emit('input', !vm.value);
                  vm.setTourCookie();
                  return this.cancel();
                },
              },
            ],
            when: {
              show() {
                if (!vm.isEmpty) {
                  vm.addArrow(this.tour, 'right');
                }
                vm.addProgressDots(this.tour);
              },
              cancel() {
                vm.closeOnboardingTag();
                vm.$emit('input', !vm.value);
                vm.setTourCookie();
              },
            },
          });
        } else {
          vm.hideClassList();

          tour.addStep({
            text: vm.onboardingStepTwoText,
            beforeShowPromise() {
              return new Promise((resolve) => {
                setTimeout(() => {
                  window.scrollTo(0, 0);
                  resolve();
                }, 500);
              });
            },
            attachTo: {
              element: this.isEmpty ? null : '.attendances__list',
              on: this.isEmpty ? null : 'right-start',
            },
            canClickTarget: false,
            modalOverlayOpeningRadius: 12,
            popperOptions: {
              placement: 'right-start',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 270],
                  },
                },
              ],
            },
            buttons: [
              {
                text: 'Rozumiem',
                action() {
                  vm.acceptOnboardingTag();
                  vm.$emit('input', !vm.value);
                  vm.showClassList();
                  vm.setTourCookie();
                  return this.cancel();
                },
              },
            ],
            when: {
              show() {
                if (!vm.isEmpty) {
                  vm.addArrow(this.tour, 'right');
                }
              },
              cancel() {
                vm.closeOnboardingTag();
                vm.showClassList();
                vm.$emit('input', !vm.value);
                vm.setTourCookie();
              },
            },
          });
        }

        tour.start();

        Event.$on('cancelTour', () => {
          tour.cancel();
        });
      });
    },
  },

  beforeDestroy() {
    Event.$off('cancelTour');
  },

  watch: {
    value(value) {
      if (value) {
        this.startOnboarding();
      }
    },
  },
};
</script>
